import ProgressBar from '@avalara/bat-apps-shared/client/components/ProgressBar';
import { SCol, SContainer, SRow } from '@avalara/skylab-sdk/react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { getPartnerSignupConnectorByCode } from '@avalara/bat-apps-shared/constants';
import { Pages } from '../../../../types/offers';
import { setReturnUrl } from '../../lib/actions';
import { getConnectors, getMarketingDataFromUrl, getQuantityFromUrl } from '../../lib/side-effects';
import { validateAndSelectConnector } from '../../lib/side-effects/offer-flow';
import { validateDiscountCode } from '../../lib/side-effects/offers';
import { stepNames } from '../../lib/side-effects/progress-bar';
import { selectState } from '../../selectors';
import { isEmbeddedSelector } from '../../selectors/offer';
import { getSelectedPlan } from '../../selectors/order';
import { currentStepLabelSelector } from '../../selectors/progress-bar';
import { setDiscountCode, setOrderQuantity } from '../../store/order';
import ContactInfo from '../contact-info/index';
import CybersourcePaymentPage from '../cybersource-payment';
import { appendQueryParamToUrl } from '../helpers/redirect';
import PageNotFound from '../page-not-found';
import LoginPrompt from '../shared/login-prompt';
import BuyButton from './buy-button';
import CompanyForm from './forms/company';
import GetStartedForm from './forms/get-started';
import OfferDetails from './offer-details';
import OrderSummary from './order-summary';
import ReviewOrder from './review-order';
import StandaloneSummary from './standalone-summary';

import SelectConnectorContainer from './select-connector-container';

const OfferComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    account: { isLoggedIn },
    offer,
    order: { isConnectorValidated },
    data: { connectors },
  } = useSelector(selectState);
  const currentStepLabel = useSelector(currentStepLabelSelector);
  const selectedPlan = useSelector(getSelectedPlan);

  const {
    adjustableQuantity,
    allowedUsers,
    attributionDefaults: { campaign_id: defaultCampaignId },
    isPaymentRequired,
    offerFlow,
    pages,
    redirectUrl,
    showEmbeddedSideContent,
    usesConnectors,
  } = offer;

  const selectedCampaignId = selectedPlan?.campaignId || defaultCampaignId;

  useEffect(() => {
    dispatch(getMarketingDataFromUrl(selectedCampaignId));
  }, [selectedCampaignId]);

  useEffect(() => {
    if (usesConnectors && !connectors.data.length) {
      dispatch(getConnectors());
    }
  }, [usesConnectors, connectors.data.length]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const partner = (searchParams.get('partner') || '').toUpperCase();
    const connectorId = getPartnerSignupConnectorByCode(partner)?.connectorId || searchParams.get('connectorId');
    dispatch(validateAndSelectConnector(connectorId));
  }, [usesConnectors, connectors.data.length > 0]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get('returnUrl');
    dispatch(setReturnUrl(returnUrl));

    if (!adjustableQuantity) return;
    const quantity = getQuantityFromUrl(location.search);
    if (quantity != null) dispatch(setOrderQuantity({ quantity, offer }));
  }, []);

  const isEmbedded = useSelector(isEmbeddedSelector);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    // NOTE(Allie): Using 'c' for 'code' aka discount code so it isn't obvious to users
    const discountCode = searchParams.get('c');

    if (discountCode != null) {
      dispatch(setDiscountCode(discountCode));
      dispatch(validateDiscountCode(offerFlow, discountCode));
    }
  }, [isLoggedIn]);

  if (redirectUrl) {
    const searchParams = new URLSearchParams(location.search);
    return <Redirect to={appendQueryParamToUrl(redirectUrl, searchParams.toString())} />;
  }

  const onPaymentStep = currentStepLabel === stepNames.paymentMethod;
  const onCreateAccountStep = currentStepLabel === stepNames.createYourAccount;
  const isOnCreateAccountStep = onCreateAccountStep;
  const showOfferDetails = !isEmbedded && !onPaymentStep && offer.offerDetails;
  const mainColumnSpan = isEmbedded ? 'auto' : 'md-7 8';
  const showSideColumn = (!isEmbedded || showEmbeddedSideContent) && !isOnCreateAccountStep;
  const mainColumnCenter = undefined;

  if (!isLoggedIn && allowedUsers === 'EXISTING') return <LoginPrompt />;
  if (!offerFlow) return <PageNotFound />;
  /**
   * Flows without an in-bundle connector step (e.g. add-integration) validate the connector via URL before
   * rendering. Flows that include SelectConnector validate at purchase when the shopper picks one on that page.
   */
  const needsPrefetchConnectorValidation = usesConnectors === 'REQUIRED' && !pages.includes(Pages.SelectConnector);
  if (needsPrefetchConnectorValidation && !isConnectorValidated) return null;

  const authenticatedRedirectPath = isPaymentRequired ? 'payment' : 'review-order';

  return (
    // biome-ignore lint/a11y/useSemanticElements: using Skylab's component
    <SContainer role='main'>
      <SRow className='pad-top-sm'>
        <SCol span={mainColumnSpan} center={mainColumnCenter}>
          <ProgressBar />
        </SCol>
      </SRow>

      <SRow>
        <SCol span={mainColumnSpan} center={mainColumnCenter}>
          <Switch>
            {!isLoggedIn && (
              <Route path={`/${offerFlow}/get-started`}>
                <GetStartedForm flow={offerFlow} />
              </Route>
            )}

            {!isLoggedIn && (
              <Route path={`/${offerFlow}/company`}>
                <CompanyForm flow={offerFlow} />
              </Route>
            )}

            {pages.includes(Pages.Payment) && (
              <Route path={`/${offerFlow}/payment`}>
                <CybersourcePaymentPage paymentView='offer' isEmbedded={isEmbedded} />
              </Route>
            )}

            {pages.includes(Pages.AddBillingContact) && (
              <Route path={`/${offerFlow}/add-billing-contact`}>
                <ContactInfo />
              </Route>
            )}

            {pages.includes(Pages.ReviewOrder) && (
              <Route path={`/${offerFlow}/review-order`}>
                <ReviewOrder />
              </Route>
            )}

            {isEmbedded && !showEmbeddedSideContent && (
              <Route path={`/${offerFlow}/summary`}>
                <StandaloneSummary />
              </Route>
            )}

            {pages.includes(Pages.SelectConnector) && (
              <Route path={`/${offerFlow}/select-connector`}>
                <SelectConnectorContainer />
              </Route>
            )}

            <Route>
              <Redirect
                to={{
                  pathname: `/${offerFlow}/${isLoggedIn ? authenticatedRedirectPath : 'get-started'}`,
                  search: location.search,
                }}
              />
            </Route>
          </Switch>

          {showOfferDetails && <OfferDetails />}
        </SCol>

        {showSideColumn && (
          <SCol span='md-5 4'>
            <OrderSummary />
            {(onPaymentStep || showEmbeddedSideContent) && <BuyButton />}
          </SCol>
        )}
      </SRow>
    </SContainer>
  );
};

export default OfferComponent;
